import {
  ButtonLink,
  Input,
  PageHeader,
  Pagination,
  RadioGroup,
  ToggleButtonGroup,
  UserRadioCard,
} from '@maia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { getCustomersOptions } from '../../../../features/customer/customers/queryOptions';
import { formatAddress } from '@maia/utils';
import { useMemo, useState } from 'react';
import { CustomerInformation } from '../../../../features/shared/customer/customer-information';
import { IconAngleRight, IconSearch } from '@maia/icons';
import { useQuery } from '@tanstack/react-query';
import { validateSearch } from '../../../../utils/pagination';
import { useFilter } from '../../../../utils/filter/filter';
import { debounce } from 'es-toolkit';
import { transformSearchToStateObject } from '../../../../utils/filter/utils';

export const Route = createFileRoute('/_authenticated/claims/new/_layout/')({
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, deps: search }) => {
    const searchOptioons = transformSearchToStateObject(search);
    return await queryClient.ensureQueryData(
      getCustomersOptions(searchOptioons),
    );
  },
  validateSearch: validateSearch,
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const [chosenCustomerGuid, setChosenCustomerGuid] = useState<string>();
  const { state, dispatch: setSearch, mappedState } = useFilter('/claims/new');

  const {
    data: { response: customers, pagination },
  } = useQuery({ ...getCustomersOptions(mappedState), initialData: data });

  const activeCustomer = useMemo(
    () => customers.find(({ guid }) => guid === chosenCustomerGuid),
    [customers, chosenCustomerGuid],
  );

  const debouncedQuery = debounce(
    (payload: string) => setSearch({ type: 'setQuery', payload }),
    500,
  );

  return (
    <>
      <PageHeader
        className="area-page-header"
        title="Selecteer een klant"
        description="Zoek in alle actieve klanten"
      />

      <div className="area-page-content content-grid">
        <div className="col-span-full">
          <div className="flex items-center gap-x-16 mb-24">
            <Input
              icon={<IconSearch />}
              placeholder="Zoeken"
              className="w-full max-w-[550px] mr-auto"
              onChange={(event) => debouncedQuery(event.target.value)}
              defaultValue={state.search}
            />
            <ToggleButtonGroup
              defaultSelectedKey={
                state.filter?.find(([key]) => key === 'status')?.[1]
              }
              items={[
                { label: 'Alle', value: '' },
                { label: 'Actief', value: 'active' },
                { label: 'Prospect', value: 'prospect' },
                { label: 'Inactief', value: 'inactive' },
              ]}
              onChange={(status) =>
                setSearch({ type: 'setFilter', payload: ['status', status] })
              }
            />
          </div>

          <RadioGroup onChange={setChosenCustomerGuid}>
            <ul className="flex flex-col gap-y-24">
              {customers.map((customer) => (
                <li key={customer.guid}>
                  <UserRadioCard
                    value={customer.guid}
                    reference={customer.reference}
                    name={customer.consumer.fullName}
                    address={formatAddress(customer.address)}
                    tag={{
                      label: customer.status,
                      status: customer.status,
                    }}
                  />
                </li>
              ))}
            </ul>
          </RadioGroup>
          <Pagination
            {...pagination}
            variant="borderless"
            onLimitChange={(limit) =>
              setSearch({ type: 'setLimit', payload: limit })
            }
            onPageChange={(page) =>
              setSearch({ type: 'paginate', payload: page })
            }
          />
        </div>
      </div>

      <div className="flex flex-col area-page-sidebar">
        {activeCustomer && <CustomerInformation customer={activeCustomer} />}

        <ButtonLink
          to="/claims/new/$customer"
          params={{ customer: activeCustomer?.guid ?? '' }}
          intent="primary"
          iconRight={IconAngleRight}
          isDisabled={!activeCustomer}
          className="w-full justify-center mt-auto"
        >
          Volgende
        </ButtonLink>
      </div>
    </>
  );
}
